import { ChevronDown } from 'lucide-react';
import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { Button } from './Button.tsx';
import type { ButtonSize, ButtonVariant } from './Button.tsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './DropdownMenu.tsx';
import { Tooltip } from './Tooltip.tsx';

export interface SplitButtonItem {
  id: string;
  label: string;
  icon?: ReactNode;
  shortcut?: string[];
  onSelect: () => void;
  disabled?: boolean;
  /** Starts a new group: a separator above it. */
  separated?: boolean;
}

export interface SplitButtonProps {
  /** Verb plus object: "New item". */
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  /** The other ways to do the same job, in the menu behind the chevron. */
  items: SplitButtonItem[];
  /** The chevron's accessible name: "More ways to add". */
  menuLabel: string;
  variant?: Extract<ButtonVariant, 'primary' | 'secondary'>;
  size?: ButtonSize;
  /** The main action's tooltip, with its shortcut. */
  hint?: string;
  shortcut?: string[];
  disabled?: boolean;
  className?: string;
}

/**
 * One action with its close relatives folded behind a chevron (components.md, Buttons): the
 * main half does the common thing, the menu holds the rest, so a page header carries one
 * primary button instead of three.
 */
export function SplitButton({ label, icon, onClick, items, menuLabel, variant = 'primary', size = 'sm', hint, shortcut, disabled, className }: SplitButtonProps) {
  const main = (
    <Button variant={variant} size={size} icon={icon} onClick={onClick} disabled={disabled} className="rounded-r-none">
      {label}
    </Button>
  );
  return (
    <span className={cn('inline-flex items-stretch', className)} data-split-button="">
      {hint ? (
        <Tooltip content={hint} shortcut={shortcut}>
          {main}
        </Tooltip>
      ) : (
        main
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={variant}
            size={size}
            disabled={disabled}
            aria-label={menuLabel}
            // The seam: a hairline in the button's own ink, so the halves read as one control.
            className="rounded-l-none border-l border-l-current/20 px-2"
            icon={<ChevronDown />}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {items.map((it) => (
            <Fragment key={it.id}>
              {it.separated && <DropdownMenuSeparator />}
              <DropdownMenuItem icon={it.icon} shortcut={it.shortcut} disabled={it.disabled} onSelect={it.onSelect}>
                {it.label}
              </DropdownMenuItem>
            </Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
